'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { SITE } from '@/app/constants';
import { useDevice, usePWACount } from '@/app/hooks';
import { cn, reportEvent } from '@/app/utils';

const PwaDialogInstalledZh = 'https://scene.vidu.zone/media-asset/061902-WHkUrjC4HGg0DNaQ.png';
const PwaDialogInstalledEn = 'https://scene.cf.vidu.studio/media-asset/062353-ZWMqchdCCm0dcM9g.png';
const PwaDialogZh = 'https://scene.vidu.zone/media-asset/061903-OezpieIRUNLsJwMi.png';
const PwaDialogEn = 'https://scene.cf.vidu.studio/media-asset/062353-ot7qgjv1pz1yw6fr.png';
const PwaDialogSafariZh = 'https://scene.vidu.zone/media-asset/061903-rBRzleX60ClCGtlw.png';
const PwaDialogSafariEn = 'https://scene.cf.vidu.studio/media-asset/062353-Nk5qZmUAYvhrNOhQ.png';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export function PWAInstallPrompt() {
  const [isOpen, setIsOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallAttempted, setIsInstallAttempted] = useState(false);
  const { isMobile, isSafari, isPWAMode } = useDevice();
  const { shouldShowPWAPrompt, markAsShown } = usePWACount();
  const t = useTranslations('PWAInstallPrompt');

  useEffect(() => {
    // 检查是否已经在 PWA 模式下
    if (isPWAMode) {
      reportEvent('enter_from_PWA');
      return;
    }

    // 只在 PC web 端显示安装提示，移动设备不显示
    if (isMobile) {
      return;
    }
    // 检查是否应该显示PWA安装提示
    if (!shouldShowPWAPrompt()) {
      return;
    }

    setIsOpen(true);
    reportEvent('PWA_exposure');
    // 监听 beforeinstallprompt 事件
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setInstallPrompt(promptEvent);
    };

    // 监听应用安装事件 可能存在无法捕获场景
    const handleAppInstalled = () => {
      setIsOpen(false);
      setInstallPrompt(null);
      markAsShown();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [shouldShowPWAPrompt, markAsShown]);

  const handleInstall = async () => {
    reportEvent('PWA_click', {
      from: 'window',
    });

    if (installPrompt) {
      try {
        await installPrompt.prompt();
        const { outcome } = await installPrompt.userChoice;

        if (outcome === 'accepted') {
          setIsOpen(false);
          // 标记已经展示过
          markAsShown();
        }
        return;
      } catch (error) {
        console.error('PWA Debug: 安装失败:', error);
      }
    }

    // 如果没有 installPrompt，显示已安装状态或手动安装指引
    if (process.env.NODE_ENV === 'development') {
      console.info('PWA Debug: 没有 beforeinstallprompt 事件，显示手动安装指引');
    }

    // 对于其他浏览器，显示已安装状态
    setIsInstallAttempted(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsInstallAttempted(false);
    // 标记已经展示过（即使用户取消也不再显示）
    markAsShown();
  };

  // 获取要显示的图片
  const getDialogImage = () => {
    const isChina = SITE === 'china';

    if (isSafari) {
      return isChina ? PwaDialogSafariZh : PwaDialogSafariEn;
    }

    if (isInstallAttempted && !installPrompt) {
      return isChina ? PwaDialogInstalledZh : PwaDialogInstalledEn;
    }

    return isChina ? PwaDialogZh : PwaDialogEn;
  };

  // 获取按钮文本和行为
  const getButtonConfig = () => {
    if (isSafari) {
      return {
        primaryText: t('gotIt'),
        showSecondary: false,
      };
    }

    if (isInstallAttempted && !installPrompt) {
      return {
        primaryText: t('gotIt'),
        showSecondary: false,
      };
    }

    return {
      primaryText: t('button'),
      showSecondary: true,
    };
  };

  // 如果已经在 PWA 模式下或是移动设备，不显示弹窗
  if (isPWAMode || isMobile) return null;

  const buttonConfig = getButtonConfig();

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
        setIsOpen(open);
      }}
    >
      <DialogContent className="w-[600px] rounded-12 p-0 md:p-0" closable={false}>
        <div className="flex flex-col items-center justify-start">
          <Image src={getDialogImage()} alt="Vidu" width={600} height={390} />
          <div
            className={cn('flex w-full flex-col items-start justify-start px-6 py-[30px]', SITE === 'global' && 'py-6')}
          >
            <p className="font-PingFangSC text-[18px] leading-[36px]">{t('title')}</p>
            <p
              className={cn(
                'font-PingFangSC text-[12px] text-system-white64 leading-[24px]',
                SITE === 'global' && 'leading-[1.4]',
              )}
            >
              {t('description')}
            </p>
          </div>
          <div className="flex w-full items-center justify-end gap-2 px-6 pb-6">
            {buttonConfig.showSecondary && (
              <Button variant="outline" onClick={handleClose} className="w-[116px] rounded-8">
                {t('cancel')}
              </Button>
            )}
            <Button
              variant="primary"
              onClick={buttonConfig.primaryText === t('gotIt') ? handleClose : handleInstall}
              className="w-[116px] rounded-8"
            >
              {buttonConfig.primaryText}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
